import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useModal } from "@root/client/modal";
import { useRetrieveTimeSheet } from "@planzone/view-models";
import { ProjectElementType } from "@planzone/dto";
import { UseCases } from "@planzone/core";
import { ResourceAvatar } from "@common/resourceAvatar";
import { useToggle, useUtils } from "@common/treeToggle";
import { Flag } from "@common/infiniteScroll";
import { Safeguard } from "@scenes/safeguard";
import { Loader } from "../../loader";
import { ListToggleArrow } from "@common/listToggleArrow";
import { RecordList } from "./recordList";
import { Props } from "./types";
import { List } from "./list";
import { Task } from "./task";
import { renderWithTreeCtx } from "./utils";
import styles from "./styles.scss";

function Allocation({
  resource,
  filter,
  week,
  project,
  areDetailsDisplayed,
  isReloading,
  showCompletionColumn,
  timesheetDeadline,
}: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { isOpen, toggle } = useToggle(resource.uuid, 0, false);
  const { isOpen: isTreeItemOpen } = useUtils();
  const {
    isRequestPending,
    currentPage,
    timeSheet,
    canLoadMore,
    isDirty,
    isRequestFailure: isTimeSheetFailure,
    retrieveTimeSheet,
  } = useRetrieveTimeSheet(
    resource,
    project,
    { ...filter, integrationScope: "PROJECT" },
    30,
  );

  React.useEffect(() => {
    !isRequestPending &&
      !isTimeSheetFailure &&
      isDirty &&
      retrieveTimeSheet({
        project,
        resource,
        filter: { ...filter, integrationScope: "PROJECT" },
        page: 0,
        size: timeSheet.elements.length + 10,
      });
  }, [isDirty, isRequestPending]);

  return (
    <div className={styles.allocation}>
      <div className={styles.main}>
        <Safeguard useCases={[UseCases.retrieveResource]}>
          {({ canRetrieveResource }) => (
            <div className={styles.resource}>
              <div className={styles.iconBox} onClick={toggle}>
                <ListToggleArrow isOpen={isOpen} />
              </div>
              <div
                className={styles.row}
                style={{
                  cursor: resource.resourceRemoved ? "default" : "pointer",
                }}
                onClick={
                  resource.resourceRemoved
                    ? undefined
                    : () =>
                        canRetrieveResource &&
                        pushView({
                          key: UseCases.retrieveResource,
                          data: resource.uuid,
                        })
                }
              >
                <div className={styles.avatar}>
                  <ResourceAvatar resource={resource} />
                </div>
                <div className={styles.col}>
                  <p
                    className={
                      resource.deactivated
                        ? `${styles.name} ${styles.deactivated}`
                        : styles.name
                    }
                  >
                    {resource.name}
                  </p>
                  {resource.profession && (
                    <p className={styles.profession}>{resource.profession}</p>
                  )}
                </div>
              </div>
              {resource.resourceRemoved && (
                <span className={styles.removedBadge}>
                  {t("timesheetPage.resourceRemoved")}
                </span>
              )}
            </div>
          )}
        </Safeguard>
        <div className={styles.recordList}>
          {!isRequestPending && (
            <RecordList
              {...resource}
              type="resource"
              resource={resource}
              project={project}
              records={[]}
              allocatedHours={resource.totalOfAllocatedHours}
              spentTime={resource.totalOfSpentTime}
              spentTimeInWeek={resource.totalOfSpentTimeInWeek}
              remainingTime={resource.totalOfRemainingTime}
              areDetailsDisplayed={areDetailsDisplayed}
              progression={resource.percentCompletion}
              isReloading={isReloading}
              showCompletionColumn={showCompletionColumn}
            />
          )}
        </div>
      </div>
      {isOpen && (
        <>
          {renderWithTreeCtx(timeSheet, isTreeItemOpen, (element) =>
            element.type === ProjectElementType.LIST ? (
              <List
                key={element.uuid}
                list={element}
                project={project}
                resource={resource}
                depth={element.depth}
                areDetailsDisplayed={areDetailsDisplayed}
                isReloading={isReloading}
                showCompletionColumn={showCompletionColumn}
                resourceRemoved={!!resource.resourceRemoved}
              />
            ) : (
              <Task
                key={element.uuid}
                task={element}
                project={project}
                resource={resource}
                depth={element.depth}
                areDetailsDisplayed={areDetailsDisplayed}
                isReloading={isReloading}
                showCompletionColumn={showCompletionColumn}
                timesheetDeadline={timesheetDeadline}
                resourceRemoved={!!resource.resourceRemoved}
              />
            ),
          )}
          {isRequestPending && !isDirty && <Loader isLoadingTs />}
          <Flag
            name={resource.uuid}
            isDisabled={isRequestPending || !canLoadMore}
            retrieveItems={() =>
              retrieveTimeSheet({
                project,
                resource,
                filter: { ...filter, integrationScope: "PROJECT" },
                page: currentPage,
                size: 30,
              })
            }
          />
        </>
      )}
    </div>
  );
}

export { Allocation };
